import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight, Truck, Store, Leaf, ShieldCheck, ChevronLeft, ChevronRight, Star, Quote, Clock, MapPin, Sparkles,
} from "lucide-react";
import api from "@/lib/api";
import { MEDIA, STORE_INFO, getStoreStatus } from "@/lib/assets";
import { ProductCard } from "@/components/ProductCard";
import { categoryIcon } from "@/lib/categoryIcons";
import { SearchSuggest } from "@/components/SearchSuggest";

const TESTIMONIALS = [
  { quote: "Finally, proper egusi and fresh yam without the long drive. Ordered at lunch, picked up after work — exactly as shown online.", name: "Amara O.", location: "Calgary, AB" },
  { quote: "Same-day delivery got my palm oil and garri to me before dinner. It's the real deal — feels like shopping back home.", name: "Kwame B.", location: "Calgary, AB" },
  { quote: "The site shows what's actually on the shelf, so no more wasted trips. Prices match in-store too.", name: "Ngozi E.", location: "Calgary, AB" },
  { quote: "Store pickup is so convenient on my way home. Everything was bagged and ready when I arrived.", name: "Tunde A.", location: "Calgary, AB" },
  { quote: "Best selection of African groceries in the city, and the staff are so warm. My go-to every week.", name: "Fatima S.", location: "Calgary, AB" },
  { quote: "Ordered plantain, jollof spice and zobo — delivered same day, fresh and well packed. Highly recommend!", name: "Chidi N.", location: "Calgary, AB" },
];
const AVATAR_TONES = ["bg-forest text-white", "bg-clay text-white", "bg-ochre text-ink", "bg-leaf text-white"];

const DISHES = [
  { name: "Jollof rice", q: "rice" },
  { name: "Egusi soup", q: "egusi" },
  { name: "Suya", q: "suya" },
  { name: "Zobo", q: "zobo" },
  { name: "Fufu & swallow", q: "fufu" },
  { name: "Plantain", q: "plantain" },
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});
const inView = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
});

const SectionHeading = ({ eyebrow, title, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-4">
    <div>
      <span className="label-eyebrow inline-flex items-center gap-2 text-clay">
        <span className="h-px w-6 bg-clay" /> {eyebrow}
      </span>
      <h2 className="mt-3 font-display text-4xl font-semibold tracking-tight text-forest lg:text-5xl">{title}</h2>
    </div>
    {children}
  </div>
);

function HeroMedia() {
  const reduce = useReducedMotion();
  const [videoOk, setVideoOk] = useState(true);
  return (
    <div className="absolute inset-0">
      <img
        src={MEDIA.hero}
        srcSet={`${MEDIA.heroSm} 1200w, ${MEDIA.hero} 2400w`}
        sizes="100vw"
        alt="A woven basket of plantains, yams, scotch bonnet peppers, palm oil, garri and fresh greens"
        className="h-full w-full object-cover object-[70%_center]"
        fetchpriority="high"
      />
      {!reduce && videoOk && (
        <video
          className="absolute inset-0 hidden h-full w-full object-cover object-[70%_center] md:block"
          src={MEDIA.heroVideo}
          poster={MEDIA.hero}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
          onError={() => setVideoOk(false)}
        />
      )}
    </div>
  );
}

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const railRef = useRef(null);
  const [rail, setRail] = useState({ left: false, right: false });
  const status = getStoreStatus();

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 8)));
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  const updateRail = () => {
    const el = railRef.current;
    if (!el) return;
    setRail({ left: el.scrollLeft > 8, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 8 });
  };

  useEffect(() => {
    updateRail();
    const el = railRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateRail, { passive: true });
    window.addEventListener("resize", updateRail);
    return () => {
      el.removeEventListener("scroll", updateRail);
      window.removeEventListener("resize", updateRail);
    };
  }, [categories]);

  const scrollRail = (dir) => {
    const el = railRef.current;
    if (el) el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.8, 600), behavior: "smooth" });
  };

  return (
    <div data-testid="home-page" className="overflow-x-clip">
      {/* HERO */}
      <section className="px-3 pt-3 sm:px-5 lg:px-8" data-testid="hero-section">
        <div className="relative mx-auto max-w-[1440px] overflow-hidden rounded-[2rem] bg-[#FAF3E7] lg:rounded-[2.5rem]">
          {/* Media — full-bleed on desktop, top block on mobile */}
          <div className="relative h-[300px] sm:h-[380px] lg:absolute lg:inset-0 lg:h-auto">
            <HeroMedia />
            <div className="absolute inset-0 hidden bg-gradient-to-r from-[#FAF3E7] from-30% via-[#FAF3E7]/75 via-45% to-transparent to-65% lg:block" />
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#FAF3E7] to-transparent lg:hidden" />
          </div>

          <div className="relative z-10 flex min-h-0 items-center px-6 pb-10 pt-2 sm:px-10 lg:min-h-[640px] lg:px-16 lg:py-20 xl:min-h-[680px]">
            <div className="max-w-xl">
              <motion.span
                {...fadeUp(0)}
                className="inline-flex items-center gap-2 rounded-full border border-forest/15 bg-white/70 px-3.5 py-1.5 text-xs font-semibold text-forest backdrop-blur"
              >
                <Leaf className="h-3.5 w-3.5 text-leaf" /> Rooted in Africa · Delivered in Calgary
              </motion.span>
              <motion.h1
                {...fadeUp(0.08)}
                className="mt-6 text-balance font-display text-[2.9rem] font-semibold leading-[0.98] tracking-tight text-forest sm:text-6xl lg:text-[4.6rem]"
              >
                The taste of home,{" "}
                <span className="relative inline-block italic text-clay">
                  now closer
                  <svg className="absolute -bottom-2 left-0 w-full text-ochre" viewBox="0 0 300 14" fill="none" preserveAspectRatio="none" aria-hidden="true">
                    <path d="M3 10C60 3 140 2 297 8" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
                  </svg>
                </span>{" "}
                than ever.
              </motion.h1>
              <motion.p {...fadeUp(0.16)} className="mt-6 max-w-md text-base leading-relaxed text-ink/70 lg:text-lg">
                From fiery suya spice to fresh yam and chilled zobo — Bambam African Mart brings the vibrant flavours of the continent to your table.
              </motion.p>

              <motion.div {...fadeUp(0.24)} className="mt-8 max-w-md">
                <SearchSuggest
                  formClassName="rounded-full border border-border bg-white py-2 pl-5 pr-2 shadow-[0_18px_40px_-20px_rgba(15,74,35,0.35)] focus-within:border-leaf"
                  dropdownClassName="left-0 w-full"
                  inputClassName="w-full py-1.5 text-[15px]"
                  placeholder="What are you cooking today?"
                  testId="hero-search-input"
                />
              </motion.div>

              <motion.div {...fadeUp(0.3)} className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to="/shop"
                  data-testid="hero-shop-btn"
                  className="group inline-flex items-center gap-2 rounded-full bg-clay px-7 py-4 text-sm font-bold text-white shadow-[0_14px_30px_-12px_rgba(200,32,43,0.8)] transition-all duration-200 hover:bg-[#A3161F] active:scale-95"
                >
                  Shop the mart
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 rounded-full border border-forest/20 bg-white/60 px-7 py-4 text-sm font-bold text-forest backdrop-blur-sm transition-colors hover:bg-white"
                >
                  Our story
                </Link>
              </motion.div>

              <motion.div {...fadeUp(0.38)} className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-ink/70">
                <span className="inline-flex items-center gap-2">
                  <span className="flex -space-x-1.5">
                    {["A", "K", "N"].map((l, i) => (
                      <span key={l} className={`flex h-7 w-7 items-center justify-center rounded-full border-2 border-[#FAF3E7] text-[11px] font-bold text-white ${["bg-forest", "bg-clay", "bg-leaf"][i]}`}>{l}</span>
                    ))}
                  </span>
                  <span className="flex">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-ochre text-ochre" />)}</span>
                  <span className="font-semibold text-forest">Loved in Calgary</span>
                </span>
              </motion.div>
            </div>
          </div>

          {/* Floating store card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="absolute bottom-8 right-8 z-10 hidden w-72 rounded-3xl border border-white/60 bg-white/85 p-4 shadow-2xl backdrop-blur-md xl:block"
          >
            <div className="flex items-center gap-3">
              <span className={`flex h-11 w-11 items-center justify-center rounded-2xl ${status.open ? "bg-leaf/15 text-forest" : "bg-clay/10 text-clay"}`}>
                <Store className="h-5 w-5" />
              </span>
              <div className="leading-tight">
                <p className="text-sm font-bold text-forest">
                  {status.label || "Our store"}{status.today ? <span className="font-medium text-muted-foreground"> · {status.today}</span> : null}
                </p>
                <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" /> {STORE_INFO.address}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="mx-auto max-w-[1440px] px-3 pt-3 sm:px-5 lg:px-8">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[
            { icon: Truck, t: "Canada-wide delivery", s: "Real-time shipping rates" },
            { icon: Store, t: "In-store & online", s: "One live inventory" },
            { icon: ShieldCheck, t: "Secure checkout", s: "Payments by Square" },
            { icon: Leaf, t: "Authentic sourcing", s: "Straight from the source" },
          ].map((v, i) => (
            <motion.div key={v.t} {...inView(i * 0.06)} className="flex items-center gap-3 rounded-3xl border border-border/70 bg-white px-4 py-4 sm:px-5">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-leaf/10 text-forest">
                <v.icon className="h-5 w-5" />
              </div>
              <div className="leading-tight">
                <p className="text-sm font-bold text-forest">{v.t}</p>
                <p className="text-xs text-muted-foreground">{v.s}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="mx-auto max-w-[1440px] px-5 py-20 lg:px-10 lg:py-24">
        <SectionHeading eyebrow="Explore the aisles" title="Shop by category">
          <div className="flex items-center gap-2">
            <button
              data-testid="category-scroll-left"
              onClick={() => scrollRail(-1)}
              disabled={!rail.left}
              aria-label="Scroll categories left"
              className="hidden h-11 w-11 items-center justify-center rounded-full border border-border bg-white text-forest transition-all hover:border-forest hover:bg-forest hover:text-white disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-forest sm:inline-flex"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              data-testid="category-scroll-right"
              onClick={() => scrollRail(1)}
              disabled={!rail.right}
              aria-label="Scroll categories right"
              className="hidden h-11 w-11 items-center justify-center rounded-full border border-border bg-white text-forest transition-all hover:border-forest hover:bg-forest hover:text-white disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-forest sm:inline-flex"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <Link to="/shop" className="ml-2 hidden shrink-0 items-center gap-1.5 text-sm font-bold text-clay hover:underline sm:inline-flex">
              View all <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </SectionHeading>

        <div className="relative mt-10">
          <div
            ref={railRef}
            data-testid="category-rail"
            className="no-scrollbar -mx-1 flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth px-1 pb-4 pt-2"
          >
            {categories.map((c, i) => {
              const [Icon, tint] = categoryIcon(c.name);
              return (
                <motion.div
                  key={c.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: Math.min(i, 6) * 0.04 }}
                  className="shrink-0 snap-start"
                >
                  <Link
                    to={`/category/${c.slug}`}
                    data-testid={`category-${c.slug}`}
                    className="group relative flex h-full min-h-[168px] w-44 flex-col rounded-3xl border border-border/70 bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:border-forest hover:bg-forest hover:shadow-[0_20px_40px_-18px_rgba(15,74,35,0.45)] sm:w-52"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className={`flex h-12 w-12 items-center justify-center rounded-2xl transition-colors group-hover:bg-white/15 group-hover:text-white ${tint}`}>
                        <Icon className="h-6 w-6" strokeWidth={1.75} />
                      </span>
                      {c.featured && (
                        <span
                          data-testid={`category-featured-${c.slug}`}
                          aria-label="Featured"
                          title="Featured"
                          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-clay text-white"
                        >
                          <Star className="h-3 w-3 fill-white" />
                        </span>
                      )}
                    </div>
                    <span className="mt-4 font-display text-[1.3rem] font-semibold leading-tight text-forest transition-colors group-hover:text-white">
                      {c.name}
                    </span>
                    <span className="mt-auto inline-flex items-center gap-1.5 pt-4 text-xs font-bold uppercase tracking-[0.14em] text-clay transition-colors group-hover:text-ochre">
                      Shop now <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </div>
          {rail.right && <div className="pointer-events-none absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-sand to-transparent" />}
          {rail.left && <div className="pointer-events-none absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-sand to-transparent" />}
        </div>
      </section>

      {/* FEATURED */}
      <section className="relative bg-white py-20 lg:py-24">
        <div className="kente-band absolute inset-x-0 top-0 opacity-90" />
        <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
          <SectionHeading eyebrow="Handpicked" title="Market favourites">
            <Link to="/shop" className="inline-flex shrink-0 items-center gap-2 rounded-full border border-forest/20 px-5 py-2.5 text-sm font-bold text-forest transition-colors hover:bg-forest hover:text-white">
              Shop all <ArrowRight className="h-4 w-4" />
            </Link>
          </SectionHeading>

          <div className="mt-10 grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 lg:grid-cols-4">
            {featured.length === 0
              ? Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[3/4] animate-pulse rounded-[1.75rem] bg-muted" />)
              : featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </div>
      </section>

      {/* COOK THE CLASSICS */}
      <section className="mx-auto max-w-[1440px] px-3 py-20 sm:px-5 lg:px-8 lg:py-24">
        <div className="grid items-stretch gap-3 lg:grid-cols-2">
          <motion.div {...inView(0)} className="relative min-h-[340px] overflow-hidden rounded-[2rem] lg:min-h-[520px]">
            <img src={MEDIA.feast} srcSet={`${MEDIA.feastSm} 700w, ${MEDIA.feast} 1400w`} sizes="(min-width:1024px) 50vw, 100vw" alt="Jollof rice with fried plantain, egusi soup, suya skewers and a glass of zobo" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute bottom-4 left-4 rounded-2xl bg-white/90 px-4 py-3 shadow-lg backdrop-blur">
              <p className="label-eyebrow text-clay">Tonight's menu</p>
              <p className="mt-1 font-display text-lg font-semibold text-forest">Jollof · Egusi · Suya · Zobo</p>
            </div>
          </motion.div>

          <motion.div {...inView(0.1)} className="relative flex flex-col justify-center overflow-hidden rounded-[2rem] bg-forest p-8 text-sand sm:p-12 lg:p-14">
            <div className="pattern-dots absolute inset-0" />
            <img src="/brand/logo-mark.png" alt="" aria-hidden="true" className="pointer-events-none absolute -right-10 -top-8 h-64 w-auto opacity-[0.08]" />
            <div className="relative">
              <span className="label-eyebrow inline-flex items-center gap-2 text-ochre"><Sparkles className="h-3.5 w-3.5" /> Cook the classics</span>
              <h2 className="mt-4 text-balance font-display text-4xl font-semibold leading-[1.05] text-white lg:text-5xl">
                Everything for the dishes you grew up on.
              </h2>
              <p className="mt-5 max-w-md leading-relaxed text-sand/75">
                Palm oil, egusi, crayfish, garri, yaji and more — find every ingredient for Sunday jollof or a weeknight pot of soup in one place.
              </p>
              <div className="mt-8 flex flex-wrap gap-2.5">
                {DISHES.map((d) => (
                  <Link
                    key={d.q}
                    to={`/shop?search=${encodeURIComponent(d.q)}`}
                    className="rounded-full border border-white/20 bg-white/5 px-4 py-2 text-sm font-semibold text-white transition-all hover:border-ochre hover:bg-ochre hover:text-ink"
                  >
                    {d.name}
                  </Link>
                ))}
              </div>
              <Link to="/shop" className="group mt-10 inline-flex items-center gap-2 rounded-full bg-ochre px-7 py-4 text-sm font-bold text-ink transition-all hover:bg-white active:scale-95">
                Browse all ingredients <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* IN-STORE MEETS ONLINE */}
      <section data-testid="instore-online-section" className="mx-auto max-w-[1440px] px-3 sm:px-5 lg:px-8">
        <div className="overflow-hidden rounded-[2rem] bg-[#FAF3E7] lg:rounded-[2.5rem]">
          <div className="grid lg:grid-cols-12">
            <div className="flex flex-col justify-center px-6 py-14 sm:px-12 lg:col-span-5 lg:py-20 lg:pl-16">
              <motion.span {...inView(0)} className="label-eyebrow inline-flex w-fit items-center gap-2 rounded-full bg-white px-4 py-1.5 text-clay">
                In-store meets online
              </motion.span>
              <motion.h2 {...inView(0.05)} className="mt-5 text-balance font-display text-4xl font-semibold leading-[1.05] tracking-tight text-forest lg:text-5xl">
                Same-day delivery across Calgary, or grab it in-store.
              </motion.h2>
              <motion.p {...inView(0.1)} className="mt-5 max-w-lg leading-relaxed text-ink/70">
                Order online and we'll deliver to your door the same day within Calgary — or pick up free at our Midlake store. Easy, fast, and always fresh from the shelves you trust.
              </motion.p>
              <motion.ul {...inView(0.15)} className="mt-7 space-y-3">
                {[
                  { icon: Truck, label: "Same-day delivery in Calgary" },
                  { icon: Store, label: "Free in-store pickup" },
                  { icon: ShieldCheck, label: "Fresh from our shelves" },
                ].map((s) => (
                  <li key={s.label} className="flex items-center gap-3 text-sm font-semibold text-forest">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-leaf shadow-sm"><s.icon className="h-4 w-4" /></span>
                    {s.label}
                  </li>
                ))}
              </motion.ul>
              <motion.div {...inView(0.2)} className="mt-9 flex flex-wrap gap-3">
                <Link to="/shop" data-testid="instore-shop-btn" className="group inline-flex items-center gap-2 rounded-full bg-clay px-7 py-4 text-sm font-bold text-white shadow-lg transition-all duration-200 hover:bg-[#A3161F] active:scale-95">
                  Start shopping <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
                <Link to="/about" className="inline-flex items-center gap-2 rounded-full border border-forest/20 bg-white px-7 py-4 text-sm font-bold text-forest transition-colors hover:border-forest">
                  Visit the store
                </Link>
              </motion.div>
            </div>

            <div className="relative grid min-h-[420px] grid-cols-5 gap-3 p-3 lg:col-span-7 lg:min-h-[560px]">
              <motion.div {...inView(0.05)} className="relative col-span-3 overflow-hidden rounded-[1.75rem]">
                <img src={MEDIA.delivery} srcSet={`${MEDIA.deliverySm} 700w, ${MEDIA.delivery} 1400w`} sizes="(min-width:1024px) 35vw, 60vw" alt="Grocery bags filled with plantains, yam, palm oil and greens on a Calgary doorstep" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
                <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-xs font-bold text-forest backdrop-blur"><Truck className="h-3.5 w-3.5 text-clay" /> At your door today</span>
              </motion.div>
              <motion.div {...inView(0.12)} className="relative col-span-2 flex flex-col justify-between overflow-hidden rounded-[1.75rem] bg-forest p-5 text-sand sm:p-7">
                <div className="pattern-dots absolute inset-0" />
                <div className="relative">
                  <span className="label-eyebrow text-ochre">Visit us</span>
                  <p className="mt-3 font-display text-2xl font-semibold leading-tight text-white sm:text-3xl">Our Midlake store</p>
                  <p className="mt-3 flex items-start gap-2 text-sm text-sand/75">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-ochre" />
                    <span>{STORE_INFO.address}<br />{STORE_INFO.city}</span>
                  </p>
                </div>
                <div className="relative mt-6 rounded-2xl bg-white/10 p-4">
                  <p className="flex items-center gap-1.5 text-sm font-bold text-white"><Clock className="h-4 w-4 text-ochre" /> {status.label || "Store hours"}</p>
                  {status.today && <p className="mt-1 text-xs text-sand/70">Today · {status.today}</p>}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="pb-4 pt-20 lg:pt-24">
        <div className="mx-auto mb-10 flex max-w-[1440px] flex-col items-center px-5 text-center">
          <div className="flex">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-5 w-5 fill-ochre text-ochre" />)}</div>
          <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight text-forest lg:text-5xl">Loved by our community</h2>
          <p className="mt-3 text-muted-foreground">Real words from neighbours who shop with us every week.</p>
        </div>
        <div
          className="marquee-mask relative w-full overflow-hidden"
          data-testid="testimonial-marquee"
          style={{ maskImage: "linear-gradient(to right, transparent, black 6%, black 94%, transparent)", WebkitMaskImage: "linear-gradient(to right, transparent, black 6%, black 94%, transparent)" }}
        >
          <div className="marquee-track gap-5 py-2 pr-5">
            {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
              <figure
                key={i}
                data-testid={`testimonial-${i}`}
                className="flex w-[330px] shrink-0 flex-col rounded-[1.75rem] border border-border/70 bg-white p-6 shadow-[0_1px_2px_rgba(15,74,35,0.04)] sm:w-[360px]"
              >
                <Quote className="h-7 w-7 fill-leaf/15 text-leaf" />
                <blockquote className="mt-3 flex-1 text-[15px] leading-relaxed text-ink/85">"{t.quote}"</blockquote>
                <figcaption className="mt-5 flex items-center gap-3 border-t border-border/70 pt-4">
                  <span className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold ${AVATAR_TONES[i % AVATAR_TONES.length]}`}>
                    {t.name.charAt(0)}
                  </span>
                  <div>
                    <p className="text-sm font-bold text-forest">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.location}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
